/**
 * ambitoRegla — el TIPO y el ÁMBITO de una regla de aviso, por separado (Fase 4, 22/10).
 *
 * Una regla guarda `asset_type_slug` y `scope` ('interno' | 'colab' | NULL = los dos ámbitos). En el
 * formulario, el tipo se elige UNA vez por slug («Coche»), y el ámbito aparte, con sólo las opciones
 * que existen para ese slug en el catálogo:
 *   · el slug existe en los dos ámbitos → GRUPOLAE, Colaboradores y «Los dos ámbitos»;
 *   · existe en uno → sólo ése.
 * Antes el tipo se elegía ya con ámbito («Coche · colab») y «Los dos ámbitos» quedaba escondido detrás.
 *
 * Sin React ni red: lo prueba `ambitoRegla.test.js` con node.
 */

export const LOS_DOS = 'los_dos';
export const ETIQUETA_AMBITO = { interno: 'GRUPOLAE', colab: 'Colaboradores', [LOS_DOS]: 'Los dos ámbitos' };

const normScope = (s) => (s === 'colab' ? 'colab' : 'interno');

/**
 * Agrupa el catálogo por slug, en el orden en que aparece cada slug por primera vez.
 * → [{ slug, nombre, porScope: { interno?, colab? }, scopes: ['interno'?, 'colab'?] }]
 */
export function agruparPorSlug(tipos) {
  const grupos = new Map();
  (tipos || []).forEach((t) => {
    if (!t || !t.slug) return;
    if (!grupos.has(t.slug)) grupos.set(t.slug, { slug: t.slug, nombre: t.nombre || t.slug, porScope: {} });
    const g = grupos.get(t.slug);
    const sc = normScope(t.scope);
    if (!g.porScope[sc]) g.porScope[sc] = t;
  });
  return [...grupos.values()].map((g) => ({ ...g, scopes: ['interno', 'colab'].filter((s) => g.porScope[s]) }));
}

/** Opciones del selector «Ámbito» para un grupo: los ámbitos que existen, y «Los dos» si son dos. */
export function opcionesAmbito(grupo) {
  if (!grupo) return [];
  const ops = grupo.scopes.map((s) => ({ id: s, nombre: ETIQUETA_AMBITO[s] }));
  if (grupo.scopes.length === 2) ops.push({ id: LOS_DOS, nombre: ETIQUETA_AMBITO[LOS_DOS] });
  return ops;
}

/** Etiqueta del desplegable de tipo: con el ámbito sólo si el tipo existe en uno. */
export function etiquetaTipo(grupo) {
  if (!grupo) return '';
  return grupo.scopes.length === 1 ? `${grupo.nombre} · ${grupo.scopes[0] === 'colab' ? 'colab' : 'GRUPOLAE'}` : grupo.nombre;
}

/**
 * Ámbito con el que se abre el formulario.
 *   · Al EDITAR: el `scope` de la regla; NULL → «Los dos» si el tipo está en los dos, o el único que hay.
 *   · Al CREAR o al cambiar de tipo: si hay un único ámbito, ése; si hay dos, ninguno (se elige a mano).
 */
export function ambitoInicial(grupo, { editando = false, scope = null } = {}) {
  if (!grupo) return '';
  if (grupo.scopes.length === 1) return grupo.scopes[0];
  if (editando) return scope === 'interno' || scope === 'colab' ? scope : LOS_DOS;
  return '';
}

/** ¿El ámbito elegido vale para ese grupo? */
export function ambitoValido(grupo, ambito) {
  return opcionesAmbito(grupo).some((o) => o.id === ambito);
}

/** `scope` que se envía: NULL sin tipo («Todos los tipos») o con «Los dos ámbitos». */
export function scopeDelPayload(grupo, ambito) {
  if (!grupo) return null;
  return ambito === 'interno' || ambito === 'colab' ? ambito : null;
}

/** Los tipos a los que alcanza la regla: el del ámbito, o los dos gemelos con «Los dos». */
export function tiposDelAmbito(grupo, ambito) {
  if (!grupo) return [];
  if (ambito === 'interno' || ambito === 'colab') return grupo.porScope[ambito] ? [grupo.porScope[ambito]] : [];
  if (ambito === LOS_DOS) return grupo.scopes.map((s) => grupo.porScope[s]);
  return [];
}

/**
 * Campos de `fields_schema` que cumplen `filtro` y existen en TODOS los tipos del ámbito (en «Los
 * dos», los comunes a los dos gemelos: una regla de fecha sobre un campo que sólo tiene uno no
 * avisaría del otro). Orden y etiqueta, los del primero. → [{ id: name, nombre: label }]
 */
export function camposDelAmbito(grupo, ambito, filtro) {
  const tipos = tiposDelAmbito(grupo, ambito);
  if (!tipos.length) return [];
  const campos = (t) => (Array.isArray(t.fields_schema) ? t.fields_schema : []).filter(filtro);
  const [primero, ...resto] = tipos;
  return campos(primero)
    .filter((f) => resto.every((t) => campos(t).some((g) => g.name === f.name)))
    .map((f) => ({ id: f.name, nombre: f.label || f.name }));
}

/** Etiqueta del ámbito de una regla en la lista: «Todos» sin tipo; NULL con tipo → «Los dos ámbitos». */
export function etiquetaAmbitoRegla(regla) {
  if (!regla || !regla.asset_type_slug) return 'Todos';
  return ETIQUETA_AMBITO[regla.scope === 'interno' || regla.scope === 'colab' ? regla.scope : LOS_DOS];
}
